import { useState } from 'react';
import { ExoCamera } from '../../components/exo/ExoCamera';
import ExoDialog from '../../components/exo/ExoDialog';
import { Box, Button, Typography, Alert, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

export const ClockOutImage = ({ open, onClose, onSubmit, required }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [showWarning, setShowWarning] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  const handleImageTaken = (response) => {
    const photoData = response?.data ?? response;
    onSubmit(photoData);
    setShowWarning(false);
  };

  const handleSkip = () => {
    if (required) {
      setShowWarning(true);
      return;
    }
    onSubmit(null);
    setShowWarning(false);
  };

  const handleCameraError = () => {
    setCameraError(true);
  };

  const handleClockOutWithoutPhoto = () => {
    onSubmit(null);
    setShowWarning(false);
    setCameraError(false);
  };

  const handleClose = () => {
    onClose();
    setShowWarning(false);
    setCameraError(false);
  };

  if (!open) return null;

  return (
    <ExoDialog open={open} onClose={handleClose} limitWidth>
      <Box className="flex flex-col gap-4 p-4">
        {/* Header */}
        <Box className="flex items-center gap-2">
          <CameraAltIcon sx={{ color: colors.info }} />
          <Typography variant="h3">{t('Clock Out Photo')}</Typography>
        </Box>

        {/* Warning */}
        {showWarning && (
          <Alert
            severity="error"
            sx={{
              borderRadius: '12px',
              '& .MuiAlert-icon': { alignItems: 'center' },
            }}
          >
            {t('A photo is required when clocking out')}
          </Alert>
        )}

        {/* Camera */}
        <Box
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            border: showWarning
              ? `2px solid ${colors.error}`
              : '2px solid transparent',
            transition: 'border-color 0.2s ease',
          }}
        >
          <ExoCamera
            fileType="CLOCK_OUT_IMAGE"
            onImageTaken={handleImageTaken}
            onError={handleCameraError}
          />
        </Box>

        {/* Skip Button */}
        {!required && (
          <Box
            className="flex"
            sx={{ justifyContent: 'flex-end' }}
          >
            <Button
              variant="outlined"
              onClick={handleSkip}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
              }}
            >
              {t('Skip')}
            </Button>
          </Box>
        )}

        {/* Camera error fallback — allow clock-out without photo */}
        {required && cameraError && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: '12px',
              '& .MuiAlert-icon': { alignItems: 'center' },
            }}
            action={
              <Button
                color="warning"
                size="small"
                onClick={handleClockOutWithoutPhoto}
                sx={{ textTransform: 'none' }}
              >
                {t('Clock out without photo')}
              </Button>
            }
          >
            {t('Camera not available. You can still clock out without a photo.')}
          </Alert>
        )}
      </Box>
    </ExoDialog>
  );
};
